import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { RfqForm } from "./leadForms";

const RfqModalContext = createContext(null);

export function useRfqModal() {
  const context = useContext(RfqModalContext);
  if (context) return context;
  return {
    openRfq: () => { window.location.href = "/request-quote"; },
    closeRfq: () => {},
  };
}

function RfqModal({ state, onClose, lastFocus }) {
  useEffect(() => {
    if (!state.open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = lastFocus.current;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    const focusTimer = setTimeout(() => {
      document.querySelector('[data-testid="rfq-modal"] input, [data-testid="rfq-modal"] textarea, [data-testid="rfq-modal"] button')?.focus();
    }, 60);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      clearTimeout(focusTimer);
      if (previousFocus && previousFocus.focus) previousFocus.focus();
    };
  }, [state.open, onClose, lastFocus]);

  if (!state.open) return null;
  return (
    <div className="rfq-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }} data-testid="rfq-modal-overlay">
      <div className="rfq-modal-panel" role="dialog" aria-modal="true" aria-labelledby="rfq-modal-title" data-testid="rfq-modal">
        <div className="rfq-modal-head">
          <div>
            <div className="eyebrow copper-text">{state.mode === "sell" ? "Sell to SJB Steels" : "Request a quote"}</div>
            <h2 id="rfq-modal-title">{state.mode === "sell" ? "Offer material." : "Tell us what you need."}<br /><em>{state.product || "Global metals."}</em></h2>
          </div>
          <button className="rfq-modal-close" onClick={onClose} aria-label="Close enquiry form" data-testid="rfq-modal-close"><X size={20} /></button>
        </div>
        <RfqForm key={`${state.mode}-${state.product}`} compact selectedProduct={state.product} initialMode={state.mode} idPrefix="modal-rfq" />
      </div>
    </div>
  );
}

export function RfqModalProvider({ children }) {
  const [state, setState] = useState({ open: false, product: "", mode: "buy" });
  const lastFocus = useRef(null);
  const closeRfq = useCallback(() => setState((current) => ({ ...current, open: false })), []);
  const openRfq = useCallback((options = {}) => {
    lastFocus.current = document.activeElement;
    setState({ open: true, product: options.product || "", mode: options.mode || "buy" });
  }, []);
  return (
    <RfqModalContext.Provider value={{ openRfq, closeRfq }}>
      {children}
      <RfqModal state={state} onClose={closeRfq} lastFocus={lastFocus} />
    </RfqModalContext.Provider>
  );
}
